import { YOU } from "@/api/memory-focus";
import { AVATAR_SIZE, type AvatarKind } from "@/lib/memory-thing-kinds";
import { detectZoomRectSemantics } from "@/lib/zoom-rect-semantics";
import { clamp, depthLook, project, type Camera } from "./globe-projection";
import { placeLabels, type PlacedLabel } from "./globe-labels";
import { strokeArcs, strokeSpokes, drawNode, drawLabel } from "./globe-draw";
import { rgba } from "./globe-palette";
import {
  DIM,
  type DrawOptions,
  type Fades,
  type GraphArea,
  type Point,
  type ProjectedNode,
  type ProjectedScene,
} from "./globe-render-types";
import type { GlobeScene, SceneArc } from "./globe-scene-types";

export { DIM } from "./globe-render-types";
export type {
  Rgba,
  GlobePalette,
  Point,
  GraphArea,
  ProjectedNode,
  ProjectedArc,
  ProjectedScene,
  Fades,
  DrawOptions,
} from "./globe-render-types";
export { parseColor, rgba, readPalette } from "./globe-palette";

/** Where the globe sits (mockup graphArea): 16px sides, 20px top, 48px for the state line, 288px more on the right while the
 * pocket is inside; the radius is .4 of the smaller side. */
export function graphArea(
  width: number,
  height: number,
  pocketInside: boolean,
): GraphArea {
  const [w, h] = [width - 32 - (pocketInside ? 288 : 0), height - 68];
  return {
    cx: 16 + w / 2,
    cy: 20 + h / 2,
    radius: Math.max(40, Math.min(w, h) * 0.4),
  };
}
export const nodeHalf = (avatar: AvatarKind) =>
  avatar === "topic" ? 4 : AVATAR_SIZE[avatar] / 2;
/** Backing pixels per CSS pixel: the display's ratio times the canvas zoom above 1 (React Flow scales the node), at most 3. */
export const backingRatio = (devicePixelRatio: number, canvasZoom: number) =>
  Math.min(3, Math.max(1, devicePixelRatio) * Math.max(1, canvasZoom));
/** A backing store of `ratio` pixels per CSS pixel; drawing and hit tests stay in CSS pixels. */
export function sizeCanvas(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D | null,
  width: number,
  height: number,
  ratio: number,
): void {
  const [w, h] = [
    Math.max(1, Math.round(width * ratio)),
    Math.max(1, Math.round(height * ratio)),
  ];
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);
}
/** A pointer in the canvas's CSS pixels, whatever the canvas zoom scales the frame by (mockup localPoint). */
export function canvasPoint(
  client: { clientX: number; clientY: number },
  rect: { left: number; top: number; width: number },
  offsetWidth: number,
  cssZoom = 1,
): Point {
  // cssZoom is the effective CSS zoom only; transform zoom needs no compensation.
  const compensation =
    detectZoomRectSemantics() === "unscaled" &&
    Number.isFinite(cssZoom) &&
    cssZoom > 0
      ? cssZoom
      : 1;
  const left = rect.left * compensation;
  const top = rect.top * compensation;
  const width = rect.width * compensation;
  const scale = offsetWidth > 0 && width > 0 ? width / offsetWidth : 1;
  return {
    x: (client.clientX - left) / scale,
    y: (client.clientY - top) / scale,
  };
}
export const initialFades = (): Fades => ({
  lit: new Map(),
  temp: new Map(),
  dimmed: 0,
});
/** Eases towards the scene (mockup globeStep): lit things to 1, the rest to DIM, new temporary things in. True while moving. */
export function stepFades(
  fades: Fades,
  scene: GlobeScene,
  dt: number,
  reduced: boolean,
): boolean {
  const k = reduced ? 1 : 1 - Math.exp(-dt / 90);
  let moving = false;
  const ease = (current: number, target: number) => {
    if (reduced) return target;
    const next =
      Math.abs(target - current) < 0.004
        ? target
        : current + (target - current) * k;
    if (next !== target) moving = true;
    return next;
  };
  fades.dimmed = ease(fades.dimmed, scene.focusKey ? 1 : 0);
  const present = new Set(scene.nodes.map((node) => node.key));
  for (const node of scene.nodes) {
    const target = node.lit ? 1 : DIM;
    fades.lit.set(node.key, ease(fades.lit.get(node.key) ?? target, target));
    if (node.temp)
      fades.temp.set(node.key, ease(fades.temp.get(node.key) ?? 0, 1));
  }
  for (const key of [...fades.lit.keys()])
    if (!present.has(key)) {
      fades.lit.delete(key);
      fades.temp.delete(key);
    }
  return moving;
}
export function projectScene(
  scene: GlobeScene,
  camera: Camera,
  fades: Fades,
): ProjectedScene {
  const nodes = scene.nodes
    .map((node): ProjectedNode => {
      const point = node.pos
        ? project(node.pos, camera)
        : { x: camera.cx, y: camera.cy, z: 0 };
      const look = node.pos ? depthLook(point.z) : { scale: 1, alpha: 1 };
      const tempAlpha = node.temp ? (fades.temp.get(node.key) ?? 0) : 1;
      return {
        ...node,
        ...point,
        half: nodeHalf(node.avatar),
        scale: look.scale,
        depthAlpha: look.alpha,
        tempAlpha,
        vis: (fades.lit.get(node.key) ?? (node.lit ? 1 : DIM)) * tempAlpha,
      };
    })
    .sort((a, b) => a.z - b.z);
  const arcs = scene.arcs.map((arc) => ({
    arc,
    points: arc.path
      ? arc.path.flatMap((p) => {
          const q = project(p, camera);
          return [q.x, q.y, q.z];
        })
      : [],
  }));
  return {
    nodes,
    byKey: new Map(nodes.map((node) => [node.key, node])),
    arcs,
    focusKey: scene.focusKey,
    dense: scene.dense,
    camera,
    dimmed: fades.dimmed,
  };
}
/** The frontmost thing under the pointer (mockup globeNodeAt); a temporary thing still fading in does not count. */
export function globeNodeAt(
  scene: ProjectedScene,
  point: Point,
): ProjectedNode | null {
  for (let index = scene.nodes.length - 1; index >= 0; index--) {
    const node = scene.nodes[index]!;
    const r =
      (node.avatar === "topic" ? 7 : node.half + 3) *
      (node.key === YOU ? 1 : node.scale);
    if (
      !(node.temp && node.tempAlpha < 0.5) &&
      Math.hypot(point.x - node.x, point.y - node.y) <= r
    )
      return node;
  }
  return null;
}
function segmentDistance(
  p: Point,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): number {
  const [dx, dy] = [x1 - x0, y1 - y0];
  const t = clamp(
    ((p.x - x0) * dx + (p.y - y0) * dy) / (dx * dx + dy * dy || 1),
    0,
    1,
  );
  return Math.hypot(p.x - (x0 + t * dx), p.y - (y0 + t * dy));
}
/** The front arc or spoke within 5px of the pointer (mockup globeFactAt); while focused, only the focus's. */
export function globeFactAt(
  scene: ProjectedScene,
  point: Point,
): SceneArc | null {
  let [best, bestDistance]: [SceneArc | null, number] = [null, 5];
  const consider = (arc: SceneArc, distance: number) => {
    if (distance < bestDistance) [best, bestDistance] = [arc, distance];
  };
  const focused = scene.focusKey !== null;
  for (const { arc, points } of scene.arcs) {
    if (points.length === 0 || (focused && !arc.focus)) continue;
    for (let i = 0; i + 5 < points.length; i += 3)
      if (points[i + 2]! >= -0.05)
        consider(
          arc,
          segmentDistance(
            point,
            points[i]!,
            points[i + 1]!,
            points[i + 3]!,
            points[i + 4]!,
          ),
        );
  }
  const you = scene.byKey.get(YOU);
  if (!you || Math.hypot(point.x - you.x, point.y - you.y) <= 18) return best;
  for (const { arc, points } of scene.arcs) {
    const other =
      points.length === 0
        ? scene.byKey.get(arc.from === YOU ? arc.to : arc.from)
        : undefined;
    if (other && other.z >= -0.05 && (!focused || other.key === scene.focusKey))
      consider(arc, segmentDistance(point, you.x, you.y, other.x, other.y));
  }
  return best;
}

/** One frame of the globe, back to front (mockup drawGlobe); returns the labels it placed. */
export function drawGlobe(
  ctx: CanvasRenderingContext2D,
  scene: ProjectedScene,
  options: DrawOptions,
): PlacedLabel[] {
  const { palette, width, height } = options;
  const { cx, cy, radius, zoom } = scene.camera;
  const you = scene.byKey.get(YOU);
  ctx.globalCompositeOperation = "source-over";
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = rgba(palette.ink, 0.03);
  ctx.beginPath();
  ctx.arc(cx, cy, radius * zoom * 1.1, 0, Math.PI * 2);
  ctx.fill();
  strokeArcs(ctx, scene, options, false);
  for (const node of scene.nodes)
    if (node.key !== YOU && node.z < 0)
      drawNode(ctx, node, scene.focusKey, palette);
  strokeSpokes(ctx, scene, options);
  if (you) {
    const glow = ctx.createRadialGradient(you.x, you.y, 0, you.x, you.y, 64);
    glow.addColorStop(0, rgba(palette.ink, 0.1 * you.vis));
    glow.addColorStop(1, rgba(palette.ink, 0));
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(you.x, you.y, 64, 0, Math.PI * 2);
    ctx.fill();
    drawNode(ctx, you, scene.focusKey, palette);
  }
  strokeArcs(ctx, scene, options, true);
  for (const node of scene.nodes)
    if (node.key !== YOU && node.z >= 0)
      drawNode(ctx, node, scene.focusKey, palette);
  const labelled = options.hoverLabelOnly
    ? scene.nodes.filter((node) => node.key === options.hoverKey)
    : scene.nodes;
  const labels = placeLabels({
    nodes: labelled.map((node) => ({
      ...node,
      half: node.half * (node.key === YOU ? 1 : node.scale),
    })),
    focusKey: scene.focusKey,
    hoverKey: options.hoverKey,
    dense: scene.dense,
    width,
    height,
    pocket: options.pocket,
    measure: (text, font) => {
      ctx.font = font;
      return ctx.measureText(text).width;
    },
  });
  for (const label of labels) {
    const node = scene.byKey.get(label.key);
    if (node) drawLabel(ctx, label, node, palette);
  }
  return labels;
}
